import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsAIConfig() {
  const [config, setConfig] = useState({
    provider: 'openai',
    api_key: '',
    ollama_base_url: '',
    ollama_mode: 'cloud',
    model: 'gpt-4',
    enabled: false,
    enable_intro: true,
    enable_line_items: true,
    enable_closing: true,
    enable_template_design: false,
    enable_rams_augment: false,
    tone: 'professional'
  });
  const [hasExistingKey, setHasExistingKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await apiFetch('/api/ai/config');
      const data = await response.json();
      // Server strips api_key for security — track existence separately
      const keyExists = data.api_key_exists || false;
      setHasExistingKey(keyExists);
      // Don't overwrite user-entered key with empty value from server
      // Only set api_key from server if it's actually present (shouldn't be)
      const { api_key, ...safeData } = data;
      // Derive ollama_mode from saved state
      if (safeData.provider === 'ollama') {
        safeData.ollama_mode = (safeData.ollama_base_url && !safeData.ollama_base_url.includes('ollama.com')) ? 'local' : 'cloud';
      }
      setConfig(prev => ({
        ...prev,
        ...safeData,
        // Keep existing user-entered key; only clear if no key exists on server
        api_key: prev.api_key || (keyExists ? '' : ''),
      }));
    } catch (error) {
      console.error('Error loading AI config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      // Clean config before saving — only send relevant fields per mode
      const saveConfig = { ...config };
      if (saveConfig.provider === 'ollama') {
        if (saveConfig.ollama_mode === 'cloud') {
          saveConfig.ollama_base_url = ''; // Cloud uses ollama.com automatically
        } else {
          saveConfig.api_key = ''; // Self-hosted doesn't need an API key
        }
        delete saveConfig.ollama_mode; // Not a DB column, just UI state
      }
      // If api_key is empty but a key exists on server, don't send empty string
      // (which would overwrite the saved key). Only send if user entered a new one.
      if (!saveConfig.api_key && hasExistingKey) {
        delete saveConfig.api_key;
      }
      const response = await apiFetch('/api/ai/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saveConfig)
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Save failed (${response.status})`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving AI config:', error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await apiFetch('/api/ai/test', { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        setTestResult({ success: true, message: data.message, model: data.model, debug: data.debug });
      } else {
        setTestResult({ success: false, message: data.error || 'Connection failed', debug: data.debug });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message || 'Network error' });
    } finally {
      setTesting(false);
    }
  };

  const fetchModels = async () => {
    setLoadingModels(true);
    setModelsError('');
    try {
      const response = await apiFetch('/api/ai/models');
      const data = await response.json();
      if (data.error) {
        setModelsError(data.error);
        setAvailableModels([]);
      } else {
        setAvailableModels(data.models || []);
        if (data.models?.length === 0) {
          setModelsError('No models found — check your API key and save first');
        }
      }
    } catch (error) {
      setModelsError(error.message || 'Failed to load models');
      setAvailableModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">AI Configuration</h2>
        <p className="text-sm text-gray-600 mt-1">
          Configure AI-powered text and template generation
        </p>
      </div>

      <div className="p-6 space-y-6">

        {/* Enable/Disable */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Enable AI Quote Generation</span>
              <p className="text-xs text-gray-600 mt-1">
                Turn on AI-powered text generation for quotes
              </p>
            </div>
          </label>
        </div>

        {/* Provider Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            AI Provider
          </label>
          <select
            value={config.provider}
            onChange={(e) => {
              const newProvider = e.target.value;
              const resets = { provider: newProvider };
              if (newProvider === 'ollama') {
                resets.model = '';
                resets.ollama_base_url = '';
                resets.ollama_mode = 'cloud';
              } else if (newProvider === 'openai') {
                resets.model = 'gpt-4';
                resets.ollama_base_url = '';
                resets.ollama_mode = '';
              } else if (newProvider === 'anthropic') {
                resets.model = 'claude-3-5-sonnet-20241022';
                resets.ollama_base_url = '';
                resets.ollama_mode = '';
              } else if (newProvider === 'openrouter') {
                resets.model = 'openai/gpt-4o-mini';
                resets.ollama_base_url = '';
                resets.ollama_mode = '';
              }
              setConfig({ ...config, ...resets });
              setAvailableModels([]);
              setModelsError('');
            }}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!config.enabled}
          >
            <option value="openai">OpenAI (GPT-4, GPT-3.5)</option>
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openrouter">OpenRouter (Multi-model, cheapest)</option>
            <option value="ollama">Ollama</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Choose your AI service provider
          </p>
        </div>

        {/* API Key */}
        {config.provider !== 'ollama' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            API Key
          </label>
          <div className="flex items-center gap-2">
            <input
              type={showApiKey ? "text" : "password"}
              value={config.api_key}
              onChange={(e) => { const val = e.target.value; setConfig({ ...config, api_key: val }); setHasExistingKey(!!val); }}
              className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={hasExistingKey ? "Key saved •••••••• (enter new to change)" : "sk-..."}
              disabled={!config.enabled}
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              {showApiKey ? '🙈 Hide' : '👁️ Show'}
            </button>
          </div>
          {hasExistingKey && !config.api_key && (
            <p className="text-xs text-green-600 mt-1">✅ API key saved — enter a new value above to change it</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {config.provider === 'openai' && 'Get your API key from platform.openai.com'}
            {config.provider === 'anthropic' && 'Get your API key from console.anthropic.com'}
            {config.provider === 'openrouter' && 'Get your API key from openrouter.ai — supports 100+ models at lower cost'}
          </p>
        </div>
        )}

        {/* Ollama Cloud/Local Toggle */}
        {config.provider === 'ollama' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Connection Type
          </label>
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setConfig({ ...config, ollama_mode: 'cloud' })}
              className={`flex-1 max-w-xs px-4 py-3 rounded-lg border-2 text-sm font-medium transition ${
                config.ollama_mode === 'cloud'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
              disabled={!config.enabled}
            >
              ☁️ Ollama Cloud
              <p className="text-xs font-normal mt-1">Use Ollama's hosted service</p>
            </button>
            <button
              type="button"
              onClick={() => setConfig({ ...config, ollama_mode: 'local', ollama_base_url: config.ollama_base_url || 'http://localhost:11434' })}
              className={`flex-1 max-w-xs px-4 py-3 rounded-lg border-2 text-sm font-medium transition ${
                config.ollama_mode === 'local'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
              disabled={!config.enabled}
            >
              🏠 Self-Hosted
              <p className="text-xs font-normal mt-1">Your own Ollama server</p>
            </button>
          </div>

          {config.ollama_mode === 'cloud' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ollama Cloud API Key
              </label>
              <div className="flex items-center gap-2">
                <input
                  type={showApiKey ? "text" : "password"}
                  value={config.api_key}
                  onChange={(e) => { const val = e.target.value; setConfig({ ...config, api_key: val }); setHasExistingKey(!!val); }}
                  className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={hasExistingKey ? "Key saved •••••••• (enter new to change)" : "Your Ollama Cloud API key"}
                  disabled={!config.enabled}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  {showApiKey ? '🙈 Hide' : '👁️ Show'}
                </button>
              </div>
              {hasExistingKey && !config.api_key && (
                <p className="text-xs text-green-600 mt-1">✅ API key saved — enter a new value above to change it</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Get your API key from ollama.com</p>
            </div>
          )}

          {config.ollama_mode === 'local' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ollama Server URL
              </label>
              <input
                type="text"
                value={config.ollama_base_url || ''}
                onChange={(e) => setConfig({ ...config, ollama_base_url: e.target.value })}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://your-ollama-server.com"
                disabled={!config.enabled}
              />
              <p className="text-xs text-gray-500 mt-1">
                URL of your Ollama server (default port: 11434). No API key needed.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Model Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model
          </label>
          <div className="flex items-center gap-2">
            {availableModels.length > 0 ? (
              <select
                value={availableModels.includes(config.model) ? config.model : ''}
                onChange={(e) => setConfig({ ...config, model: e.target.value })}
                className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={!config.enabled}
              >
                <option value="">Select a model...</option>
                {availableModels.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={config.model}
                onChange={(e) => setConfig({ ...config, model: e.target.value })}
                className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={config.provider === 'ollama' ? 'llama3, mistral, codellama...' : 'Model name'}
                disabled={!config.enabled}
              />
            )}
            <button
              type="button"
              onClick={fetchModels}
              disabled={loadingModels || !config.enabled}
              className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
              title="Refresh model list"
            >
              {loadingModels ? '⏳' : '🔄'}
            </button>
          </div>
          {modelsError && (
            <p className="text-xs text-yellow-600 mt-1">⚠️ {modelsError}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {availableModels.length > 0
              ? `${availableModels.length} models available`
              : 'Save config first, then 🔄 to load models'}
          </p>
        </div>

        {/* Enable by Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Enable AI for:
          </label>
          <div className="space-y-2">
            <label className="flex items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer transition">
              <input
                type="checkbox"
                checked={config.enable_intro}
                onChange={(e) => setConfig({ ...config, enable_intro: e.target.checked })}
                className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={!config.enabled}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Quote Introduction</span>
                <p className="text-xs text-gray-600">
                  Generate professional opening paragraphs for quotes
                </p>
              </div>
            </label>

            <label className="flex items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer transition">
              <input
                type="checkbox"
                checked={config.enable_line_items}
                onChange={(e) => setConfig({ ...config, enable_line_items: e.target.checked })}
                className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={!config.enabled}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Line Item Descriptions</span>
                <p className="text-xs text-gray-600">
                  Expand brief notes into detailed technical descriptions
                </p>
              </div>
            </label>

            <label className="flex items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer transition">
              <input
                type="checkbox"
                checked={config.enable_closing}
                onChange={(e) => setConfig({ ...config, enable_closing: e.target.checked })}
                className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={!config.enabled}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Quote Closing</span>
                <p className="text-xs text-gray-600">
                  Generate professional closing paragraphs with terms
                </p>
              </div>
            </label>

            <label className="flex items-center px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-lg cursor-pointer transition border border-purple-200">
              <input
                type="checkbox"
                checked={config.enable_template_design}
                onChange={(e) => setConfig({ ...config, enable_template_design: e.target.checked })}
                className="mr-3 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                disabled={!config.enabled}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">✨ AI Template Designer</span>
                <p className="text-xs text-gray-600">
                  Generate marketing email templates from descriptions — AI creates editable canvas designs
                </p>
              </div>
            </label>

            <label className="flex items-center px-4 py-3 bg-orange-50 hover:bg-orange-100 rounded-lg cursor-pointer transition border border-orange-200">
              <input
                type="checkbox"
                checked={config.enable_rams_augment}
                onChange={(e) => setConfig({ ...config, enable_rams_augment: e.target.checked })}
                className="mr-3 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                disabled={!config.enabled}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">⚠️ RAMS AI Augmentation</span>
                <p className="text-xs text-gray-600">
                  Suggest site-specific risks and method steps for draft RAMS — engineer reviews before applying
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Tone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tone
          </label>
          <select
            value={config.tone}
            onChange={(e) => setConfig({ ...config, tone: e.target.value })}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!config.enabled}
          >
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="technical">Technical</option>
            <option value="formal">Formal</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Writing style for generated text
          </p>
        </div>

        {/* Warning */}
        {config.enabled && config.provider !== 'ollama' && !config.api_key && !hasExistingKey && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-yellow-600 text-lg mr-2">⚠️</span>
              <div>
                <p className="text-sm font-medium text-yellow-800">API Key Required</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Please enter your API key to use AI generation features
                </p>
              </div>
            </div>
          </div>
        )}
        {config.enabled && config.provider === 'ollama' && !config.ollama_base_url && !config.api_key && !hasExistingKey && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-yellow-600 text-lg mr-2">⚠️</span>
              <div>
                <p className="text-sm font-medium text-yellow-800">Ollama Configuration Required</p>
                <p className="text-xs text-yellow-700 mt-1">
                  {config.ollama_mode === 'cloud'
                    ? 'Enter your Ollama Cloud API key'
                    : 'Enter your Ollama server URL'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Save & Test */}
        <div className="flex items-center space-x-3 pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          {saved && (
            <span className="text-sm text-green-600 font-medium">✓ Saved successfully</span>
          )}
          <button
            onClick={handleTest}
            disabled={testing || !config.enabled}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 font-medium ml-auto"
          >
            {testing ? 'Testing...' : '🔌 Test Connection'}
          </button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`mt-3 p-4 rounded-lg border ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-start">
              <span className="text-lg mr-2">{testResult.success ? '✅' : '❌'}</span>
              <div>
                <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                </p>
                <p className={`text-xs mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResult.message}
                </p>
                {testResult.model && (
                  <p className="text-xs text-green-600 mt-1">
                    Model: {testResult.model}
                  </p>
                )}
                {testResult.debug && !testResult.success && (
                  <div className="text-xs text-red-500 mt-2 border-t border-red-200 pt-2 font-mono">
                    <p>hasApiKey: {String(testResult.debug.hasApiKey)} | keyLength: {testResult.debug.apiKeyLength} | prefix: {testResult.debug.apiKeyPrefix}</p>
                    <p>ollamaBaseUrl: {testResult.debug.ollamaBaseUrl}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SettingsAIConfig;
